import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Bookkeeping() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [formData, setFormData] = useState({ type: 'income', amount: '', category: '', description: '', date: new Date().toISOString().split('T')[0] });

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      const { data } = await axios.get(`${API}/bookkeeping`, { withCredentials: true });
      setEntries(data);
    } catch (error) {
      toast.error('Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  const handleAISuggest = async () => {
    if (!formData.description || !formData.amount) {
      toast.error('Enter description and amount first');
      return;
    }
    setAiSuggesting(true);
    try {
      const { data } = await axios.post(`${API}/ai/categorize-expense`,
        { description: formData.description, amount: parseFloat(formData.amount), type: formData.type },
        { withCredentials: true }
      );
      setFormData({ ...formData, category: data.category });
      toast.success(`AI suggested: ${data.category}`);
    } catch (e) {
      toast.error('AI suggestion failed');
    } finally {
      setAiSuggesting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bookkeeping`, formData, { withCredentials: true });
      toast.success('Entry created');
      setShowDialog(false);
      setFormData({ type: 'income', amount: '', category: '', description: '', date: new Date().toISOString().split('T')[0] });
      loadEntries();
    } catch (error) {
      toast.error('Failed to create entry');
    }
  };

  const totalIncome = entries.filter(e => e.type === 'income').reduce((sum, e) => sum + e.amount, 0);
  const totalExpenses = entries.filter(e => e.type === 'expense').reduce((sum, e) => sum + e.amount, 0);

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="bookkeeping-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="bookkeeping-title">
            Bookkeeping
          </h1>
          <p className="mt-2 text-zinc-600">Track your income and expenses</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-black text-white hover:bg-zinc-800 gap-2" data-testid="add-entry-button">
              <Plus size={18} /> Add Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Add Entry</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="type" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Type</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                  <SelectTrigger className="mt-2" data-testid="entry-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="amount" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  required
                  data-testid="entry-amount-input"
                  className="mt-2 border-zinc-300 hover:border-black transition-colors"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="category" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Category</Label>
                  <button type="button" onClick={handleAISuggest} disabled={aiSuggesting} className="text-xs font-semibold text-blue-700 hover:underline flex items-center gap-1" data-testid="ai-suggest-category-button">
                    <Sparkle size={12} weight="fill" />{aiSuggesting ? 'Thinking...' : 'AI suggest'}
                  </button>
                </div>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  required
                  data-testid="entry-category-input"
                  className="mt-2 border-zinc-300 hover:border-black transition-colors"
                />
              </div>
              <div>
                <Label htmlFor="description" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  data-testid="entry-description-input"
                  className="mt-2 border-zinc-300 hover:border-black transition-colors"
                />
              </div>
              <div>
                <Label htmlFor="date" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                  data-testid="entry-date-input"
                  className="mt-2 border-zinc-300 hover:border-black transition-colors"
                />
              </div>
              <Button type="submit" className="w-full bg-black text-white hover:bg-zinc-800" data-testid="submit-entry-button">
                Create Entry
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 border border-zinc-200" data-testid="total-income-card">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-600">Total Income</p>
          <p className="mt-2 text-3xl font-bold text-green-600" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>R {totalIncome.toLocaleString()}</p>
        </div>
        <div className="bg-white p-6 border border-zinc-200" data-testid="total-expenses-card">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-600">Total Expenses</p>
          <p className="mt-2 text-3xl font-bold text-red-600" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>R {totalExpenses.toLocaleString()}</p>
        </div>
        <div className="bg-white p-6 border border-zinc-200" data-testid="net-profit-card">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-600">Net Profit</p>
          <p className="mt-2 text-3xl font-bold text-zinc-900" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>R {(totalIncome - totalExpenses).toLocaleString()}</p>
        </div>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="bookkeeping-table">
            <thead className="border-b border-zinc-200 bg-zinc-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Date</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Type</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Category</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Description</th>
                <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-zinc-600">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {entries.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-zinc-500" data-testid="no-entries">
                    No entries yet. Add your first entry to get started.
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-zinc-50 transition-colors" data-testid={`entry-row-${entry.id}`}>
                    <td className="px-6 py-4 text-zinc-600 text-sm">{new Date(entry.date).toLocaleDateString()}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold border ${
                        entry.type === 'income' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'
                      }`}>
                        {entry.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-zinc-900">{entry.category}</td>
                    <td className="px-6 py-4 text-zinc-600 text-sm">{entry.description || 'N/A'}</td>
                    <td className="px-6 py-4 text-right font-semibold text-zinc-900">R {entry.amount.toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
